import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, CheckCircle2, KeyRound, Mail, MapPin, Search, User, Users } from "lucide-react";
import { AppHeader } from "../components/AppHeader";
import { DemoBanner, DemoCoachmark, DemoFooter } from "../components/DemoBanner";
import { Button } from "../components/ui/button";
import { getDemoTokens, getDemoTourStep, setDemoTourStep, skipDemoTour } from "../lib/demoSession";

export default function DemoDashboard({ firstName = "Dr Alex Morgan", nav, linkMode = false, exitPath = "/clinicians", onExit, scope }) {
  const [tokens, setTokens] = useState(() => getDemoTokens(scope));
  const [tourStep, setTourStep] = useState(() => getDemoTourStep(scope));

  useEffect(() => {
    const refresh = (event) => {
      if (!event.detail || event.detail.scope === scope) setTokens(getDemoTokens(scope));
    };
    const refreshTour = (event) => {
      if (!event.detail || event.detail.scope === scope) setTourStep(getDemoTourStep(scope));
    };
    window.addEventListener("simplyrefer-demo-tokens", refresh);
    window.addEventListener("simplyrefer-demo-tour", refreshTour);
    return () => {
      window.removeEventListener("simplyrefer-demo-tokens", refresh);
      window.removeEventListener("simplyrefer-demo-tour", refreshTour);
    };
  }, [scope]);

  const startTour = () => {
    setDemoTourStep(scope, 1);
    setTourStep(1);
  };
  const skipTour = () => {
    skipDemoTour(scope);
    setTourStep(99);
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader firstName={firstName} role="gp" sampleMode demoLinkMode={linkMode} demoNav={nav} demoScope={scope} />
      <DemoBanner kind="dashboard" linkMode={linkMode} exitPath={exitPath} onExit={onExit} />
      <main className="mx-auto max-w-7xl px-4 py-9 sm:px-6 lg:px-8 animate-fadein" data-testid="demo-dashboard-page">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">Welcome, {firstName}</h1>
            <p className="mt-1 text-sm text-slate-500">Explore a fictional GP account and see how SimplyRefer fits into a normal referral workflow.</p>
          </div>
          <Button asChild size="lg"><Link to={nav.clinicians} onClick={startTour} data-testid="demo-start-find-clinician"><Search className="mr-2 h-5 w-5" /> Find a clinician</Link></Button>
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="demo-three-steps">
          <p className="font-heading text-base font-semibold text-slate-900">Try SimplyRefer in three quick steps</p>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <JourneyStep number="1" title="Find a fictional clinician" text="Use the directory filters to find a suitable match." />
            <JourneyStep number="2" title="Simply Refer" text="Select for Referral, create an optional SimplyRefer Reference, or open a prefilled email." />
            <JourneyStep number="3" title="Continue your usual process" text="Use your existing system or attach your referral letter to the email." />
          </div>
        </div>

        {tourStep === 0 && (
          <DemoCoachmark title="Start here" onSkip={skipTour} className="mt-5">
            Select <strong>Find a clinician</strong>. We’ll guide you through the directory one step at a time.
          </DemoCoachmark>
        )}

        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-4">
          <InfoCard icon={User} label="Demo GP" value={firstName} />
          <InfoCard icon={Mail} label="Email" value="alex.morgan@example.com" />
          <InfoCard icon={Building2} label="Clinic" value="Harbour Family Practice" />
          <InfoCard icon={KeyRound} label="References this session" value={String(tokens.length)} />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Link to={nav.clinicians} onClick={startTour} className="group flex items-center justify-between rounded-md border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid="demo-dashboard-clinicians">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary text-secondary-foreground"><Users className="h-5 w-5" /></div>
              <div>
                <p className="font-heading text-base font-semibold text-slate-900">Find a clinician</p>
                <p className="text-sm text-slate-500">Try combinations of specialties, locations and availability.</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-primary transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to={nav.referrals} className="group flex items-center justify-between rounded-md border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid="demo-dashboard-referrals">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-blue-50 text-primary"><KeyRound className="h-5 w-5" /></div>
              <div>
                <p className="font-heading text-base font-semibold text-slate-900">SimplyRefer References</p>
                <p className="text-sm text-slate-500">Review the fictional references created in this browser session.</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-primary transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="mt-6 rounded-md border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-900">Recent demo activity</h2>
              <p className="text-sm text-slate-500">Only activity from this demo session appears here.</p>
            </div>
            <MapPin className="h-5 w-5 text-slate-300" />
          </div>
          {tokens.length === 0 ? (
            <p className="mt-4 rounded-md bg-slate-50 p-4 text-sm text-slate-500">No demo references yet. Open Clinicians and create one to see it appear here.</p>
          ) : (
            <div className="mt-4 space-y-2">
              {tokens.slice(0, 3).map((token) => (
                <div key={token.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-4 py-3 text-sm">
                  <span className="font-medium text-slate-800">{token.clinician.first_name} {token.clinician.last_name}</span>
                  <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 font-mono text-xs font-semibold text-blue-700">#{token.token}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <DemoFooter linkMode={linkMode} exitPath={exitPath} onExit={onExit} />
      </main>
    </div>
  );
}

const JourneyStep = ({ number, title, text }) => (
  <div className="flex gap-3 rounded-lg bg-slate-50 p-3">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">{number}</div>
    <div><p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><CheckCircle2 className="h-4 w-4 text-primary" /> {title}</p><p className="mt-0.5 text-xs leading-relaxed text-slate-600">{text}</p></div>
  </div>
);

const InfoCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border border-slate-200 bg-white p-5">
    <div className="flex items-center gap-2 text-slate-500"><Icon className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">{label}</span></div>
    <p className="mt-2 break-words font-heading text-lg font-semibold text-slate-900">{value}</p>
  </div>
);
